/*!
 * Copyright (c) REWE Digital GmbH
 */
import { Host, h } from "@stencil/core";
import paybackLogo from "./assets/payback_logo.svg";
/**
 * Displays the payback point information alongside the payback logo.
 * Includes a tooltip with more information about the payback points.
 *
 * @example
 * <vx-payback info="Du erhältst" pointText="Payback Punkte" points="100" tooltipHeadline="Payback Punkte" tooltipText="Du erhältst" tooltipCta="Mehr erfahren" policy="https://www.payback.de/"></vx-payback>
 */
// eslint-disable-next-line require-jsdoc
export class VxPayback {
    constructor() {
        this.registerLink = undefined;
        this.info = undefined;
        this.pointText = '';
        this.points = undefined;
        this.tooltipHeadline = undefined;
        this.tooltipText = undefined;
        this.tooltipCta = undefined;
        this.infoCardOpen = false;
    }
    /**
     * @inheritDoc
     */
    render() {
        /**
         * Function component to represent the points with unit.
         */
        const points = () => h("span", { key: '29416ff1481abdc20f7e27d46f21dfa8e9e73ca3', innerHTML: this.points + ' ' + this.pointText });
        return (h(Host, { key: 'cc9a38e86f854c7e8058d683333dafb580013920' }, h("div", { key: '4cdb16a8873c99bcc4467d22da002f9592f52ee1', class: "vx-payback-inner" }, h("img", { key: '61ef17c5c3b6d1be6af3dd690c4b7696ccb0b9fc', alt: "Payback Logo", class: "vx-payback-logo", src: paybackLogo }), h("div", { key: '46dbf7749183d940e674ad923df5dac1d1b5a7d7', class: "vx-payback-text-container" }, this.info && h("p", null, this.info), this.points && h("p", { class: "vx-payback-points" }, points()))), h("div", { key: '88c37bf9c2b443570ab154917d32fe2ff802d6c5', class: "info-circle", onClick: () => { this.infoCardOpen = !this.infoCardOpen; } }, h("span", { key: '7fcd642bb8fbf21ff63682c81e1279bfc6e5c751', class: "info-icon" }, "i")), h("vx-popover", { key: 'd4ac026b4bd4aad7191f9d63b3572ecd604c6be6', isOpen: this.infoCardOpen }, h("div", { key: 'c2f2fa2bd7233260ae8caf9ac6f2c1d9e0d4981c', class: "info-tooltip" }, h("p", { key: '25f2aef12ce98de926eb4ce062c036223f4d44eb', class: "info-tooltip-headline" }, this.tooltipHeadline), h("div", { key: '945524db24518b93bfa3298625ab173059cc5f20', class: "info-tooltip-text" }, this.tooltipText, h("p", { key: '50730cca432452d8a867157d5a69793f88484e7f', class: "payback-points" }, points())), h("vx-button", { key: 'e97e3f55f8de5ceb6e2ea2db6b0563737a465e3b', class: "info-tooltip-button", "aria-label": "Payback Informationen", label: this.tooltipCta, raised: true, onClick: () => (window.location.href = this.registerLink) }), h("img", { key: 'f1766422f652aa92e78c74c7c33243b0638886dc', alt: "Payback Logo", class: "info-tooltip-logo vx-payback-logo", src: paybackLogo })))));
    }
    static get is() { return "vx-payback"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["vx-payback.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["vx-payback.css"]
        };
    }
    static get properties() {
        return {
            "registerLink": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The URL to the Register page."
                },
                "attribute": "register-link",
                "reflect": false
            },
            "info": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The info text before the points are displayed."
                },
                "attribute": "info",
                "reflect": false
            },
            "pointText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "The text after the points are displayed."
                },
                "attribute": "point-text",
                "reflect": false,
                "defaultValue": "''"
            },
            "points": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The amount of points."
                },
                "attribute": "points",
                "reflect": false
            },
            "tooltipHeadline": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The headline of the tooltip."
                },
                "attribute": "tooltip-headline",
                "reflect": false
            },
            "tooltipText": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The text of the tooltip."
                },
                "attribute": "tooltip-text",
                "reflect": false
            },
            "tooltipCta": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The CTA text of the tooltip."
                },
                "attribute": "tooltip-cta",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "infoCardOpen": {}
        };
    }
}
//# sourceMappingURL=vx-payback.js.map
